import { ApolloError } from '@apollo/client';
import { GraphQLError as ApolloGraphQLError } from 'graphql';
import React, { FC, useEffect } from 'react';

import { useToasts } from '@sbu/contexts.toasts';

export const mergeErrorMessages = (
  errors?: ReadonlyArray<ApolloGraphQLError>,
  defaultMessage = ''
) => {
  let joinedMessages = defaultMessage;

  if (errors) {
    const joined = errors
      .map(({ message }) => message)
      .filter((message) => message !== 'Request not authorized')
      .join(' ');

    if (joined.length > 0) {
      joinedMessages = joined;
    }
  }
  return joinedMessages;
};

interface ICheckError {
  ({ networkError, graphQLErrors }: ApolloError, throwError?: boolean): string;
}

export const checkError: ICheckError = ({ networkError, graphQLErrors }) => {
  let message = 'Something went wrong...';

  // Network errors are not tracked on Sentry.
  if (networkError) {
    message = 'Something went wrong when attempting to fetch resource';

    const extendedNetworkError = networkError as any;
    if (extendedNetworkError.result) {
      message = mergeErrorMessages(extendedNetworkError.result.errors, message);
    }
  }

  if (graphQLErrors?.length) {
    message = mergeErrorMessages(graphQLErrors, message);
  }
  return message;
};

interface GraphqlErrorHandlerProps {
  error?: ApolloError;
  onError?: (error: ApolloError) => boolean;
  stopPolling?: () => void;
}

export const GraphqlErrorHandler: FC<GraphqlErrorHandlerProps> = ({
  children,
  error,
  onError,
  stopPolling,
}) => {
  const { addError } = useToasts();

  useEffect(() => {
    if (error && !onError?.(error)) {
      addError(checkError(error));

      // Stop polling (if applicable) when an error is encountered.
      stopPolling?.();
    }
  }, [addError, error, onError, stopPolling]);

  return <>{children}</>;
};
